import { RootLayout } from '@/routes/__root'
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
} from '@tanstack/react-router'

const AuthCallbackRoute = lazyRouteComponent(() => import('@/routes/_auth/callback'))
const LoginRoute = lazyRouteComponent(() => import('@/routes/_auth/login'))
const AccountingUsersRoute = lazyRouteComponent(
  () => import('@/routes/_dashboard/accounting-users'),
)
const AdminsRoute = lazyRouteComponent(() => import('@/routes/_dashboard/admins'))
const CustomerApplicationsRoute = lazyRouteComponent(
  () => import('@/routes/_dashboard/customer-applications'),
)
const CustomersRoute = lazyRouteComponent(() => import('@/routes/_dashboard/customers'))
const DigitalDevelopmentUsersRoute = lazyRouteComponent(
  () => import('@/routes/_dashboard/digital-development-users'),
)
const ErpCustomerFieldsRoute = lazyRouteComponent(
  () => import('@/routes/_dashboard/erp/customer-fields'),
)
const FormTemplatesRoute = lazyRouteComponent(() => import('@/routes/_dashboard/form-templates'))
const FormTemplateBuilderRoute = lazyRouteComponent(
  () => import('@/routes/_dashboard/form-templates/$templateId/edit'),
)
const FormBuilderRoute = lazyRouteComponent(() => import('@/routes/_dashboard/forms/$formId/edit'))
const FormsRoute = lazyRouteComponent(() => import('@/routes/_dashboard/forms/index'))
const DashboardHomeRoute = lazyRouteComponent(() => import('@/routes/_dashboard/index'))
const IntegrationsRoute = lazyRouteComponent(() => import('@/routes/_dashboard/integrations'))
const DashboardLayout = lazyRouteComponent(() => import('@/routes/_dashboard/layout'))
const QuestionGroupsRoute = lazyRouteComponent(() => import('@/routes/_dashboard/question-groups'))
const ReviewCenterRoute = lazyRouteComponent(() => import('@/routes/_dashboard/review-center'))
const ReviewNotificationsRoute = lazyRouteComponent(
  () => import('@/routes/_dashboard/review-notifications'),
)
const SalesRoute = lazyRouteComponent(() => import('@/routes/_dashboard/sales'))
const SettingsRoute = lazyRouteComponent(() => import('@/routes/_dashboard/settings'))
const SyncCenterRoute = lazyRouteComponent(() => import('@/routes/_dashboard/sync-center'))
const SystemRoute = lazyRouteComponent(() => import('@/routes/_dashboard/system'))
const WarehouseRoute = lazyRouteComponent(() => import('@/routes/_dashboard/warehouse'))
const WarehouseExportsRoute = lazyRouteComponent(
  () => import('@/routes/_dashboard/warehouse-exports'),
)
const WarehouseLogisticsCompareRoute = lazyRouteComponent(
  () => import('@/routes/_dashboard/warehouse-logistics-compare'),
)
const WarehouseUsersRoute = lazyRouteComponent(() => import('@/routes/_dashboard/warehouse-users'))
const ErpBackfillCompareActionRoute = lazyRouteComponent(
  () => import('@/routes/erp-backfill/compare-actions/$token'),
)
const IndexRoute = lazyRouteComponent(() => import('@/routes/index'))
const LoginAdminRoute = lazyRouteComponent(() => import('@/routes/login-admin'))
const DirectReviewApproveRoute = lazyRouteComponent(() => import('@/routes/review-links/approve'))
const DirectReviewQuestionnaireRoute = lazyRouteComponent(
  () => import('@/routes/review-links/questionnaire'),
)
const LoginSuperRoute = lazyRouteComponent(() => import('@/routes/super-login'))
const LegacySuperLoginRedirect = lazyRouteComponent(
  () => import('@/routes/super-login'),
  'LegacySuperLoginRedirect',
)
const PublicFormRoute = lazyRouteComponent(() => import('@/routes/to/$formId/index'))

function RouteLoadingFallback() {
  return (
    <output
      className="grid min-h-dvh place-items-center bg-slate-50 p-6 text-center text-sm text-slate-600"
      aria-live="polite"
    >
      正在載入頁面...
    </output>
  )
}

const rootRoute = createRootRoute({ component: RootLayout })

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: IndexRoute,
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginRoute,
})

const callbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/callback',
  component: AuthCallbackRoute,
})

const loginSuperRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login-super',
  component: LoginSuperRoute,
})

const legacySuperLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/super-login',
  component: LegacySuperLoginRedirect,
})

const loginAdminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login-admin',
  component: LoginAdminRoute,
})

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard',
  component: DashboardLayout,
})

const dashboardIndexRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/',
  component: DashboardHomeRoute,
})

const formsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/forms',
  component: FormsRoute,
})

const questionGroupsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/question-groups',
  component: QuestionGroupsRoute,
})

const formTemplatesRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/form-templates',
  component: FormTemplatesRoute,
})

const customersRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/customers',
  component: CustomersRoute,
})

const customerApplicationsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/customer-applications',
  component: CustomerApplicationsRoute,
})

const reviewCenterRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/reviews',
  component: ReviewCenterRoute,
})

const reviewCenterAliasRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/review-center',
  component: ReviewCenterRoute,
})

const reviewNotificationsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/review-notifications',
  component: ReviewNotificationsRoute,
})

const salesRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/sales',
  component: SalesRoute,
})

const warehouseRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/warehouse',
  component: WarehouseRoute,
})

const warehouseExportsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/warehouse-exports',
  component: WarehouseExportsRoute,
})

const warehouseLogisticsCompareRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/warehouse-logistics-compare',
  component: WarehouseLogisticsCompareRoute,
})

const adminsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/admins',
  component: AdminsRoute,
})

const digitalDevelopmentUsersRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/digital-development-users',
  component: DigitalDevelopmentUsersRoute,
})

const warehouseUsersRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/warehouse-users',
  component: WarehouseUsersRoute,
})

const accountingUsersRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/accounting-users',
  component: AccountingUsersRoute,
})

const syncCenterRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/sync-center',
  component: SyncCenterRoute,
})

const formBuilderRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/forms/$formId/edit',
  component: FormBuilderRoute,
})

const formTemplateBuilderRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/form-templates/$templateId/edit',
  component: FormTemplateBuilderRoute,
})

const integrationsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/integrations',
  component: IntegrationsRoute,
})

const settingsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/settings',
  component: SettingsRoute,
})

const systemRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/system',
  component: SystemRoute,
})

const systemShareRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/system/share',
  component: SystemRoute,
})

const systemNavigationRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/system/navigation',
  component: SystemRoute,
})

const systemFormsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/system/forms',
  component: SystemRoute,
})

const systemCustomersRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/system/customers',
  component: SystemRoute,
})

const systemRuntimeRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/system/runtime',
  component: SystemRoute,
})

const erpCustomerFieldsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/erp/customer-fields',
  component: ErpCustomerFieldsRoute,
})

const publicFormRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/to/$formId',
  component: PublicFormRoute,
})

const directReviewQuestionnaireRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/review-links/questionnaire',
  component: DirectReviewQuestionnaireRoute,
})

const directReviewApproveRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/review-links/approve',
  component: DirectReviewApproveRoute,
})

const erpBackfillCompareActionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/erp-backfill/compare-actions/$token',
  component: ErpBackfillCompareActionRoute,
})

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  loginSuperRoute,
  legacySuperLoginRoute,
  loginAdminRoute,
  callbackRoute,
  dashboardRoute.addChildren([
    dashboardIndexRoute,
    formsRoute,
    formTemplatesRoute,
    questionGroupsRoute,
    customersRoute,
    warehouseRoute,
    warehouseExportsRoute,
    warehouseLogisticsCompareRoute,
    customerApplicationsRoute,
    reviewCenterRoute,
    reviewCenterAliasRoute,
    reviewNotificationsRoute,
    salesRoute,
    digitalDevelopmentUsersRoute,
    accountingUsersRoute,
    warehouseUsersRoute,
    syncCenterRoute,
    adminsRoute,
    formBuilderRoute,
    formTemplateBuilderRoute,
    erpCustomerFieldsRoute,
    integrationsRoute,
    settingsRoute,
    systemRoute,
    systemNavigationRoute,
    systemShareRoute,
    systemFormsRoute,
    systemCustomersRoute,
    systemRuntimeRoute,
  ]),
  publicFormRoute,
  erpBackfillCompareActionRoute,
  directReviewQuestionnaireRoute,
  directReviewApproveRoute,
])

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPendingMs: 150,
  defaultPendingMinMs: 300,
  defaultPendingComponent: RouteLoadingFallback,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
